import { MathUtils } from 'three'
import type { Vector3Like } from 'three'
import type { Product } from './types'

/**
 * Depth of the directional fan cage in inches (clearance cylinder height)
 */
export const CAGE_DEPTH_IN = 36

/**
 * Computes the nudge offset for a directional fan to prevent its bounding
 * cylinder from clipping into the wall/roof (ceiling-mounted) when the fan is tilted.
 *
 * Should not be applied to the actual Redux store value.
 *
 * @param product - The product to compute the nudge for
 * @param sizeInches - Fan cage diameter in inches
 * @returns Nudge vector in model units
 */
export function computeDirectionalFanNudge(
  product: Pick<Product, 'rotation' | 'isForcedWallMount' | 'isDirectionalOverhead'>,
  sizeInches: number
): Vector3Like {
  const { rotation, isForcedWallMount, isDirectionalOverhead } = product
  const cageRadius = sizeInches / 2

  if (isForcedWallMount) {
    const x_rad = MathUtils.degToRad(rotation.x)
    const z_rad = MathUtils.degToRad(rotation.z)

    // Outward nudge: cage rim clips wall by R·|cos(rotation.x)| when tilted.
    const wallMagnitude = cageRadius * Math.abs(Math.cos(x_rad))

    // Downward nudge: with the pivot at the cage center, the cylinder's maximum
    // upward extent from the pivot when tilted is:
    //   (CAGE_DEPTH/2)·|cos(rotation.x)| + R·|sin(rotation.x)|
    // The baseline when horizontal (rotation.x = 90°) is R. The extra extent
    // due to tilt drives the downward nudge (clamped to ≥ 0).
    const cageHalfDepth = CAGE_DEPTH_IN / 2
    const verticalExtra = Math.max(
      0,
      cageHalfDepth * Math.abs(Math.cos(x_rad)) + cageRadius * Math.abs(Math.sin(x_rad)) - cageRadius
    )

    return {
      x: -Math.sin(z_rad) * wallMagnitude,
      y: Math.cos(z_rad) * wallMagnitude,
      z: -verticalExtra,
    }
  }

  if (isDirectionalOverhead) {
    const x_rad = MathUtils.degToRad(rotation.x)
    const magnitude = cageRadius * Math.abs(Math.sin(x_rad))
    return { x: 0, y: 0, z: -magnitude }
  }

  return { x: 0, y: 0, z: 0 }
}
